import { ChatController } from './ChatController/ChatController';
import { observer } from 'mobx-react-lite';
import { action, makeObservable } from 'mobx';
import {
  ClearHistoryCallback,
  MessageSubmitCallback,
  TChatControllerState,
} from './ChatController/types';
import { IconButton, TMenuItem } from '@apia/components';
import { openFloatingChatController } from './ChatController/openFloatingChatController';
import { TChatFeedbackForm } from './ChatController/TChatFeedbackForm';

/**
 * Floating chat controller that opens the shared chat renderer inside a modal window.
 */
export class FloatingChatController extends ChatController<{
  isWindowOpen: boolean;
}> {
  private modalTitle;
  private rememberOpenState = false;
  constructor(props: {
    props: Partial<
      TChatControllerState & {
        feedbackForm: TChatFeedbackForm;
        isWindowOpen: boolean;
      }
    >;
    onMessageSubmit: MessageSubmitCallback;
    onClearHistory: ClearHistoryCallback;
    onStop?: () => Promise<unknown> | unknown;
    rememberOpenState?: boolean;
    modalTitle?: string;
    additionalOptions?: TMenuItem[];
    classNames?: {
      history?: string;
      textarea?: string;
      attachments?: string;
    };
  }) {
    super(
      { isWindowOpen: false, ...props.props },
      props.onMessageSubmit,
      props.onClearHistory,
      props.additionalOptions,
      props.classNames,
      props.onStop,
    );
    this.rememberOpenState = props.rememberOpenState ?? false;
    this.modalTitle = props.modalTitle;
    makeObservable({ open: action });

    if (props.props.isWindowOpen) {
      this.state.isWindowOpen = false;
      this.open();
    }
  }

  /**
   * Returns the current modal title used when opening the floating chat window.
   */
  public getModalTitle() {
    return this.modalTitle;
  }

  /**
   * Changes the modal title used by the next floating chat window opening call.
   */
  public setModalTitle(title: string) {
    this.modalTitle = title;
  }

  /**
   * Opens the chat modal and persists the requested open state when configured.
   */
  open() {
    if (this.rememberOpenState) {
      sessionStorage[window.TAB_ID] = true;
    }
    if (!this.state.isWindowOpen) {
      this.state.isWindowOpen = true;

      openFloatingChatController({
        children: <this.Renderer />,
        onClose: () => {
          if (this.rememberOpenState) {
            sessionStorage[window.TAB_ID] = false;
          }
          this.state.isWindowOpen = false;
        },
        title: this.modalTitle!,
      });
    }
  }

  Window = observer(() => {
    return this.state.isWindowOpen ? null : (
      <IconButton
        className="chat_controller__open"
        icon="Magic"
        size="Md"
        variant="icon-outline"
        onClick={() => {
          this.open();
        }}
      />
    );
  });
}
